import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { formatBRL } from "@/lib/formatters";
import { queryKeys } from "@/lib/queryKeys";
import {
  type AllUsersCreditsResponse,
  type ClubCreditsResponse,
  type CreditOp,
  type UserCreditsResponse,
  creditsProvider,
} from "@/providers/credits.provider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

export const CREDIT_OPS: { value: CreditOp; label: string; hint: string }[] = [
  {
    value: "floor",
    label: "Raise to at least",
    hint: "Only tops up balances below the amount. Safe to repeat — anyone already at or above it is left alone, and gets no notification.",
  },
  {
    value: "add",
    label: "Add",
    hint: "Grants the amount on top of the current balance. Not repeatable — sending it twice grants twice.",
  },
  {
    value: "set",
    label: "Set to exactly",
    hint: "Overwrites the balance. Balances above the amount are reduced — those users are not notified. There is no undo.",
  },
];

export type CreditsTarget =
  | { kind: "user"; id: string; name?: string }
  | { kind: "club"; id: string; name?: string; memberCount?: number }
  | { kind: "all"; estimatedAudience?: number };

const ALL_USERS_PHRASE = "ALL_USERS";
const NOTE_MAX_LENGTH = 500;

/** 20,000 credits cost R$ 1 — centavos, so the amount divides by 200. */
export const CREDITS_PER_BRL = 20_000;

export function creditsToCentavos(credits: number) {
  return (credits / CREDITS_PER_BRL) * 100;
}

type CreditsResult =
  | UserCreditsResponse
  | ClubCreditsResponse
  | AllUsersCreditsResponse;

export function CreditsDialog({
  open,
  onOpenChange,
  target,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: CreditsTarget;
}) {
  const queryClient = useQueryClient();
  const [op, setOp] = useState<CreditOp>("floor");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [typedPhrase, setTypedPhrase] = useState("");

  const isClub = target.kind === "club";
  const isAll = target.kind === "all";
  const targetLabel = isAll
    ? "every user on the platform"
    : (target.name ?? (isClub ? "this club" : "this user"));
  const audience = isAll
    ? target.estimatedAudience
    : isClub
      ? target.memberCount
      : undefined;

  const parsed = Number(amount);
  const amountValid =
    amount.trim() !== "" && Number.isInteger(parsed) && parsed >= 1;
  const trimmedNote = note.trim();
  const noteValid =
    trimmedNote.length >= 1 && trimmedNote.length <= NOTE_MAX_LENGTH;
  // The API's own guard is the confirm field; make the admin type it too, since
  // a mistyped platform-wide `set` can only be undone by a database restore.
  const phraseValid = !isAll || typedPhrase.trim() === ALL_USERS_PHRASE;

  function reset() {
    setOp("floor");
    setAmount("");
    setNote("");
    setConfirming(false);
    setTypedPhrase("");
  }

  const adjust = useMutation({
    mutationFn: (): Promise<CreditsResult> => {
      const body = { op, amount: parsed, note: trimmedNote };
      switch (target.kind) {
        case "user":
          return creditsProvider.adjustUser(target.id, body);
        case "club":
          return creditsProvider.adjustClub(target.id, body);
        case "all":
          return creditsProvider.adjustAllUsers(body);
      }
    },
    onSuccess: (result) => {
      if ("credits" in result) {
        toast.success(
          `${targetLabel} now has ${result.credits.toLocaleString()} credits.`,
        );
        if (target.kind === "user") {
          queryClient.invalidateQueries({ queryKey: queryKeys.user(target.id) });
        }
      } else {
        // users_updated counts balances that actually changed, not audience
        // size — anyone already at the target is skipped, and that is success,
        // not partial failure.
        const scope = isAll ? "users" : `members of ${targetLabel}`;
        toast.success(
          result.users_updated === 0
            ? `No balances needed changing${isAll ? "" : ` in ${targetLabel}`}.`
            : `Updated ${result.users_updated.toLocaleString()} ${scope}. Notifications are delivered in the background.`,
        );
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.users() });
      queryClient.invalidateQueries({
        queryKey:
          target.kind === "user"
            ? queryKeys.userCreditLedger(target.id)
            : queryKeys.userCreditLedger(),
      });
      reset();
      onOpenChange(false);
    },
    onError: (error: Error) =>
      toast.error(error.message || "Could not adjust credits."),
  });

  function handleReview() {
    if (!amountValid) {
      toast.error(
        parsed === 0
          ? "Amount must be at least 1 — zeroing a balance isn't available here."
          : "Enter a whole, positive amount.",
      );
      return;
    }
    if (!noteValid) {
      toast.error("Add a note explaining why you're making this change.");
      return;
    }
    setConfirming(true);
  }

  const opMeta = CREDIT_OPS.find((o) => o.value === op)!;
  const destructive = op === "set" || isAll;

  const description = isAll
    ? "Applies to every non-deleted account on the platform. There is no preview and no undo — a mistake here needs a database restore."
    : isClub
      ? "Applies to every member of the club — owner, admins and members alike. Deleted accounts are excluded. There is no preview and no undo."
      : "Adjusts this user's credit balance. The user gets a push and in-app notification as soon as it lands.";

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {confirming
              ? "Confirm credit change"
              : `Adjust credits — ${targetLabel}`}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {!confirming ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label>Operation</Label>
              <Select value={op} onValueChange={(v) => setOp(v as CreditOp)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CREDIT_OPS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{opMeta.hint}</p>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="credits-amount">Amount</Label>
              <Input
                id="credits-amount"
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="30000"
              />
              <p className="text-xs text-muted-foreground">
                Whole credits, 1 or more.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="credits-note">
                Note <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="credits-note"
                required
                rows={2}
                maxLength={NOTE_MAX_LENGTH}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Compensation for failed event boost"
              />
              <div className="flex items-start justify-between gap-2 text-xs text-muted-foreground">
                <p className="min-w-0">
                  Why you're making this change. Saved to each affected user's
                  credit ledger with your admin ID.
                </p>
                <span className="shrink-0 tabular-nums">
                  {trimmedNote.length}/{NOTE_MAX_LENGTH}
                </span>
              </div>
            </div>

            <p className="text-xs text-muted-foreground border-l-2 border-muted pl-2">
              Anyone whose balance actually goes up gets a push and in-app
              notification in their own language, naming the credits gained and
              linking to the player.
              {!isClub && !isAll
                ? ""
                : " Bulk grants are queued, so notifications land shortly after this returns."}
            </p>

            {(isClub || isAll) && (
              <p className="text-xs text-muted-foreground border-l-2 border-muted pl-2">
                {isAll
                  ? "This is not atomic per user and cannot be rolled back."
                  : "The club operation is not atomic per member and cannot be rolled back."}
                {audience !== undefined
                  ? ` About ${audience.toLocaleString()} ${
                      isAll ? "accounts" : "members"
                    } are in scope.`
                  : ""}
              </p>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="rounded-lg border p-3 text-sm flex flex-col gap-2">
              <div className="flex items-center justify-between gap-4">
                <span className="text-muted-foreground">
                  {isAll ? "Scope" : isClub ? "Club" : "User"}
                </span>
                <span className="font-medium truncate">{targetLabel}</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-muted-foreground">Operation</span>
                <span className="font-medium">{opMeta.label}</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-muted-foreground">Amount</span>
                <span className="font-medium tabular-nums">
                  {parsed.toLocaleString()} credits
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-muted-foreground">Note</span>
                <p className="break-words whitespace-pre-wrap">{trimmedNote}</p>
              </div>
              {audience !== undefined && (
                <div className="flex items-center justify-between gap-4">
                  <span className="text-muted-foreground">
                    Estimated audience
                  </span>
                  <span className="font-medium tabular-nums">
                    up to {audience.toLocaleString()}{" "}
                    {isAll ? "accounts" : "members"}
                  </span>
                </div>
              )}
              {isAll && audience !== undefined && (
                <div className="flex items-center justify-between gap-4">
                  <span className="text-muted-foreground">Estimated cost</span>
                  <span className="font-medium tabular-nums">
                    {op === "add" ? "≈ " : "up to "}
                    {formatBRL(creditsToCentavos(parsed * audience))}
                  </span>
                </div>
              )}
            </div>

            {isAll && (
              <p className="text-xs text-muted-foreground border-l-2 border-muted pl-2">
                {CREDITS_PER_BRL.toLocaleString()} credits ≈ R$ 1 per user, so{" "}
                {parsed.toLocaleString()} credits costs about{" "}
                {formatBRL(creditsToCentavos(parsed))} per user
                {audience !== undefined
                  ? op === "add"
                    ? ` across ${audience.toLocaleString()} accounts.`
                    : ` — the total lands lower, since only users below ${parsed.toLocaleString()} are topped up.`
                  : "."}
              </p>
            )}

            <p className="text-xs text-muted-foreground border-l-2 border-muted pl-2">
              {op === "set"
                ? `Every affected balance becomes exactly ${parsed.toLocaleString()} — higher balances are reduced, and those users get no notification. This cannot be undone.`
                : op === "add"
                  ? `Adds ${parsed.toLocaleString()} on top of the current balance, and notifies each recipient. Applying it twice grants twice.`
                  : `Tops up anyone below ${parsed.toLocaleString()} and notifies them. Higher balances are left untouched.`}
            </p>

            <div className="rounded-lg border p-3 flex flex-col gap-1.5">
              <p className="text-xs text-muted-foreground">
                {isClub || isAll
                  ? "Each recipient whose balance rises is notified:"
                  : "The user is notified:"}
              </p>
              <p className="text-sm">
                “
                {op === "add"
                  ? `${parsed.toLocaleString()} credits have been added to your account`
                  : "<credits gained> credits have been added to your account"}
                ”
              </p>
              <p className="text-xs text-muted-foreground">
                Push and in-app, in the recipient's own language, opening the
                player screen. The number is what they gained, not their new
                balance
                {op === "add"
                  ? "."
                  : " — so it varies per user with this operation."}
              </p>
            </div>

            {isAll && (
              <div className="flex flex-col gap-2">
                <Label htmlFor="credits-confirm">
                  Type {ALL_USERS_PHRASE} to confirm
                </Label>
                <Input
                  id="credits-confirm"
                  value={typedPhrase}
                  autoComplete="off"
                  onChange={(e) => setTypedPhrase(e.target.value)}
                  placeholder={ALL_USERS_PHRASE}
                />
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() =>
              confirming ? setConfirming(false) : onOpenChange(false)
            }
            disabled={adjust.isPending}
          >
            {confirming ? "Back" : "Cancel"}
          </Button>
          {!confirming ? (
            <Button onClick={handleReview} disabled={!amountValid || !noteValid}>
              Review
            </Button>
          ) : (
            <Button
              variant={destructive ? "destructive" : "default"}
              onClick={() => adjust.mutate()}
              disabled={adjust.isPending || !phraseValid || !noteValid}
            >
              {adjust.isPending ? "Applying…" : "Apply"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
